import { useState, useCallback, useMemo, useEffect } from 'react';
import { Dialog, Button, TextField, Textarea, useDialog, SelectField } from '@workspace/design-system/src/components';
import { Channel } from '@src/models/channel';
import { PlanConfirmDialog } from '../components/plan-confirm-dialog';
import { ChannelInfoTable } from '../components/channel-info-table';
import { PLAN_OPTIONS } from '../constants';
import { useChannelContact, useChannelDetail } from '@src/hooks/channel/queries';

interface PlanNoPaymentFormModalProps {
    channel: Channel;
    mode: 'apply' | 'extend'; // 적용 또는 연장 모드
    currentEndDate?: string; // 연장 모드일 때 현재 만료일 (YYYY-MM-DD)
    onClose: () => void;
    onSubmit: (formData: PlanNoPaymentFormData) => void | Promise<void>;
    onRefresh?: () => void | Promise<void>; // 성공 시 리프래시 콜백
}

export interface PlanNoPaymentFormData {
    planType: string; // 적용 모드에서만 사용
    durationMonths: string;
    note: string;
}

export function PlanNoPaymentFormModal({
    channel,
    mode,
    currentEndDate,
    onClose,
    onSubmit,
    onRefresh
}: PlanNoPaymentFormModalProps) {
    const { openDialog, closeDialog } = useDialog();

    // 채널 상세 정보 조회
    const { data: channelDetail } = useChannelDetail(channel.channelId);
    // 고객 정보 조회
    const { data: contactData } = useChannelContact(channel.channelId);

    // 실제 사용할 채널 (채널 상세 정보 + 고객 정보 포함)
    const effectiveChannel = useMemo(() => {
        let result = { ...channel };
        if (channelDetail) {
            // channelDetail의 channelName이 있으면 사용, 없으면 원래 channel의 channelName 유지
            result = { 
                ...result, 
                ...channelDetail,
                channelName: channelDetail.channelName || channel.channelName
            };
        }
        if (contactData) {
            // contactData는 고객 정보만 포함하므로, 고객 정보 필드만 선택적으로 병합
            // 채널 정보 필드(channelId, channelName 등)는 덮어쓰지 않음
            result = {
                ...result,
                customerName: contactData.customerName ?? result.customerName,
                businessType: contactData.businessType ?? result.businessType,
                contactName: contactData.contactName ?? result.contactName,
                contactTitle: contactData.contactTitle ?? result.contactTitle,
                contactEmail: contactData.contactEmail ?? result.contactEmail,
                contactPhone: contactData.contactPhone ?? result.contactPhone,
                memo: contactData.memo ?? result.memo,
            };
        }
        return result;
    }, [channel, channelDetail, contactData]);

    const [formData, setFormData] = useState<PlanNoPaymentFormData>({
        planType: '',
        durationMonths: '',
        note: '',
    });

    // effectiveChannel이 준비되면 formData의 planType 초기화 (연장 모드일 때만)
    useEffect(() => {
        if (mode === 'extend' && effectiveChannel.plan && !formData.planType) {
            setFormData(prev => ({
                ...prev,
                planType: effectiveChannel.plan || '',
            }));
        }
    }, [mode, effectiveChannel.plan, formData.planType]);

    // 모드에 따른 레이블
    const isExtendMode = mode === 'extend';
    const modalTitle = isExtendMode ? '요금제 연장하기' : '요금제 적용';
    const durationLabel = isExtendMode ? '연장 기간' : '구매 기간';
    const submitButtonText = isExtendMode ? '요금제 연장' : '요금제 적용하기';

    // 사용 기간 계산
    const calculatedPeriod = useMemo(() => {
        if (!formData.durationMonths) return { startDate: '', endDate: '' };
        const months = parseInt(formData.durationMonths, 10);
        if (isNaN(months) || months < 1) return { startDate: '', endDate: '' };

        let startDate: Date;

        if (isExtendMode && currentEndDate) {
            // 연장 모드: 현재 만료일부터 시작
            startDate = new Date(currentEndDate);
        } else {
            // 적용 모드: 오늘부터 시작
            startDate = new Date();
        }

        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + months);

        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
    }, [formData.durationMonths, isExtendMode, currentEndDate]);

    const handleInputChange = useCallback((field: keyof PlanNoPaymentFormData, value: string) => {
        // 기간은 1 이하로 내려갈 수 없음
        if (field === 'durationMonths' && value) {
            const num = parseInt(value, 10);
            if (isNaN(num)) {
                value = '';
            } else {
                const clamped = Math.max(1, Math.min(60, num));
                value = clamped.toString();
            }
        }
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleSubmit = useCallback(async () => {
        // 적용 모드: 요금제, 구매 기간, 비고 필수
        // 연장 모드: 연장 기간, 비고 필수
        if (mode === 'apply') {
            if (!formData.planType || !formData.durationMonths || !formData.note.trim()) {
                alert('필수 항목을 모두 입력해주세요.');
                return;
            }
        } else {
            if (!formData.durationMonths || !formData.note.trim()) {
                alert('필수 항목을 모두 입력해주세요.');
                return;
            }
        }

        // 확인 다이얼로그 표시
        openDialog(
            <PlanConfirmDialog
                mode={mode}
                channelName={effectiveChannel.channelName}
                planType={mode === 'apply' ? formData.planType : (effectiveChannel.plan || '')}
                durationMonths={mode === 'extend' ? formData.durationMonths : undefined}
                onClose={closeDialog}
                onConfirm={async () => {
                    await onSubmit(formData);
                    closeDialog();
                    // 성공 시 리프래시 콜백 호출
                    if (onRefresh) {
                        await onRefresh();
                    }
                    onClose();
                }}
            />
        );
    }, [formData, mode, effectiveChannel, openDialog, closeDialog, onSubmit, onClose]);

    // 필수 필드 검증
    const isFormValid = useMemo(() => {
        if (mode === 'apply') {
            return formData.planType && formData.durationMonths && formData.note.trim();
        } else {
            return formData.durationMonths && formData.note.trim();
        }
    }, [formData, mode]);

    return (
        <Dialog>
            <Dialog.Content className="max-w-[1200px] max-h-[840px] flex flex-col">
                <Dialog.Header title={modalTitle} type="question" />

                <Dialog.Body>
                    {/* 메인 컨텐츠 */}
                    <div className="flex gap-6 flex-1 min-h-0">
                        {/* 왼쪽: 채널 정보 */}
                        <ChannelInfoTable channel={effectiveChannel} />

                        {/* 세로 구분선 */}
                        <div className="flex items-center justify-center w-px h-[568px]">
                            <div className="bg-[#F0F0F0] h-full w-px" />
                        </div>

                        {/* 오른쪽: 요금제 정보 */}
                        <div className="flex-1 flex flex-col gap-6">
                            
                         

                            {/* 요금제 (적용 모드에서만 표시) */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">요금제</label>
                                </div>
                                <div className="flex-1">
                                    <SelectField
                                        items={PLAN_OPTIONS.filter((item) => {
                                            // 항상 제외: free, realworld
                                            if (item.value === 'free' || item.value === 'realworld') {
                                                return false;
                                            }
                                            // 연장 모드일 때는 pro도 제외
                                            if (isExtendMode && item.value === 'pro') {
                                                return false;
                                            }
                                            return true;
                                        })}
                                        value={formData.planType}
                                        onSelect={(value) => handleInputChange('planType', value as string)}
                                        placeholder="이 채널에 적용하려는 요금제를 선택해 주세요"
                                        readonly={true}
                                        disabled={isExtendMode}
                                    />
                                </div>
                            </div>

                            {/* 구매 기간 / 연장 기간 */}
                            <div className="flex gap-[10px] items-start h-10">
                                <div className="flex gap-6 w-[200px] items-start">
                                    <div className="pt-[10px] w-[88px]">
                                        <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">{durationLabel}</label>
                                    </div>
                                    <div className="flex-1">
                                        <TextField
                                            placeholder="12"
                                            value={formData.durationMonths}
                                            onChange={(value) => handleInputChange('durationMonths', value)}
                                        />
                                    </div>
                                </div>
                                <div className="pt-[10px] flex items-center gap-2">
                                    <span className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">개월</span>
                                    {calculatedPeriod.endDate && (
                                        <span className="text-[14px] text-[#3C78F4] leading-[20px] tracking-[-0.1px]">
                                            (사용 기간: {isExtendMode ? `${calculatedPeriod.startDate}부터` : '오늘부터'} {calculatedPeriod.endDate} 까지)
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* 비고 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">
                                        메모
                                    </label>
                                </div>
                                <div className="flex-1">
                                    <Textarea
                                        placeholder={isExtendMode ? "연장 사유를 입력해 주세요." : "특이사항을 입력해 주세요."}
                                        value={formData.note}
                                        onChange={(e) => handleInputChange('note', e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Body>

                <Dialog.Footer className="border-t border-[#F0F0F0] h-[72px]">
                    <Button styleType="outline-basic" styleSize="M-40" onClick={onClose}>
                        닫기
                    </Button>
                    <Button
                        styleType="primary"
                        styleSize="M-40"
                        onClick={handleSubmit}
                        disabled={!isFormValid}
                    >
                        {submitButtonText}
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}

